import React, { memo, useCallback, useMemo } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';

import { FeedbackAdminScopeOption } from '../../../types';
import './AdminFeedbackFilters.css';

export interface AdminFeedbackFiltersValue {
  scopeKey: string;
  minRating: number | '';
  maxRating: number | '';
  dateFrom: string;
  dateTo: string;
  anonymous: 'all' | 'yes' | 'no';
}

export const DEFAULT_FILTERS: AdminFeedbackFiltersValue = {
  scopeKey: 'ALL',
  minRating: '',
  maxRating: '',
  dateFrom: '',
  dateTo: '',
  anonymous: 'all',
};

interface AdminFeedbackFiltersProps {
  value: AdminFeedbackFiltersValue;
  onChange: (next: AdminFeedbackFiltersValue) => void;
  scopeOptions: FeedbackAdminScopeOption[];
  loadingScopes?: boolean;
  totalResults?: number;
  isFetching?: boolean;
}

const groupScopeOptions = (options: FeedbackAdminScopeOption[]) => {
  const ungrouped: FeedbackAdminScopeOption[] = [];
  const groups: { label: string; items: FeedbackAdminScopeOption[] }[] = [];
  const groupIndex = new Map<string, number>();

  for (const opt of options) {
    if (!opt.optgroup) {
      ungrouped.push(opt);
      continue;
    }
    let idx = groupIndex.get(opt.optgroup);
    if (idx === undefined) {
      idx = groups.length;
      groupIndex.set(opt.optgroup, idx);
      groups.push({ label: opt.optgroup, items: [] });
    }
    groups[idx].items.push(opt);
  }

  return { ungrouped, groups };
};

const AdminFeedbackFilters: React.FC<AdminFeedbackFiltersProps> = memo(({
  value,
  onChange,
  scopeOptions,
  loadingScopes = false,
  totalResults,
  isFetching = false,
}) => {
  const handleField = useCallback(
    <K extends keyof AdminFeedbackFiltersValue>(
      key: K,
      next: AdminFeedbackFiltersValue[K],
    ) => {
      onChange({ ...value, [key]: next });
    },
    [onChange, value],
  );

  const handleReset = useCallback(() => {
    onChange(DEFAULT_FILTERS);
  }, [onChange]);

  const { ungrouped, groups } = useMemo(
    () => groupScopeOptions(scopeOptions),
    [scopeOptions],
  );

  return (
    <Form
      className="admin-feedback-filters"
      onSubmit={(e) => e.preventDefault()}
      role="search"
      aria-label="Filtros de feedback"
    >
      <Row className="g-3">
        <Col xs={12} md={6} lg={4}>
          <Form.Group controlId="filter-scope">
            <Form.Label>Escopo</Form.Label>
            <Form.Select
              value={value.scopeKey}
              onChange={(e) => handleField('scopeKey', e.target.value)}
              disabled={loadingScopes}
              aria-describedby="filter-scope-help"
            >
              {ungrouped.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              {groups.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.items.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Form.Select>
            <Form.Text id="filter-scope-help" className="text-muted">
              Mesmas opções do formulário de feedback (menus, relatórios e fluxos).
            </Form.Text>
          </Form.Group>
        </Col>

        <Col xs={6} md={3} lg={2}>
          <Form.Group controlId="filter-min-rating">
            <Form.Label>Nota mín.</Form.Label>
            <Form.Select
              value={value.minRating === '' ? '' : String(value.minRating)}
              onChange={(e) =>
                handleField('minRating', e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              <option value="">Qualquer</option>
              {[1, 2, 3, 4, 5].map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col xs={6} md={3} lg={2}>
          <Form.Group controlId="filter-max-rating">
            <Form.Label>Nota máx.</Form.Label>
            <Form.Select
              value={value.maxRating === '' ? '' : String(value.maxRating)}
              onChange={(e) =>
                handleField('maxRating', e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              <option value="">Qualquer</option>
              {[1, 2, 3, 4, 5].map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col xs={12} md={6} lg={4}>
          <Form.Group controlId="filter-anonymous">
            <Form.Label>Anonimato</Form.Label>
            <Form.Select
              value={value.anonymous}
              onChange={(e) =>
                handleField('anonymous', e.target.value as AdminFeedbackFiltersValue['anonymous'])
              }
            >
              <option value="all">Todos</option>
              <option value="no">Identificados</option>
              <option value="yes">Anônimos</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col xs={12} md={6} lg={3}>
          <Form.Group controlId="filter-date-from">
            <Form.Label>De</Form.Label>
            <Form.Control
              type="date"
              value={value.dateFrom}
              max={value.dateTo || undefined}
              onChange={(e) => handleField('dateFrom', e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={6} lg={3}>
          <Form.Group controlId="filter-date-to">
            <Form.Label>Até</Form.Label>
            <Form.Control
              type="date"
              value={value.dateTo}
              min={value.dateFrom || undefined}
              onChange={(e) => handleField('dateTo', e.target.value)}
            />
          </Form.Group>
        </Col>

        <Col xs={12} className="d-flex align-items-end justify-content-between flex-wrap gap-2">
          <div aria-live="polite" className="admin-feedback-filters__status text-muted small">
            {isFetching ? (
              <span>
                <i className="bi bi-arrow-repeat me-1 spin" aria-hidden="true"></i>
                Atualizando resultados...
              </span>
            ) : typeof totalResults === 'number' ? (
              <span>
                <strong>{totalResults}</strong> resultado{totalResults === 1 ? '' : 's'} encontrado
                {totalResults === 1 ? '' : 's'}
              </span>
            ) : null}
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            type="button"
            onClick={handleReset}
            disabled={isFetching}
          >
            <i className="bi bi-eraser me-1" aria-hidden="true"></i>
            Limpar filtros
          </Button>
        </Col>
      </Row>
    </Form>
  );
});

AdminFeedbackFilters.displayName = 'AdminFeedbackFilters';

export default AdminFeedbackFilters;
